/**
 * Progressive reveal for the reviews list.
 *
 * Every review is already in the page: this only hides the ones past the first batch and hands them
 * back a batch at a time. That way the list still works with no JavaScript at all — it just shows
 * everything — and revealing costs no request.
 *
 * The batch is smaller on a phone than on a desktop, because a desktop screen swallows more cards
 * before the button comes back into view.
 *
 * Vanilla: no imports, no framework, no theme runtime.
 */
if (!customElements.get('sustena-reviews-more')) {
  class SustenaReviewsMore extends HTMLElement {
    #desktop = window.matchMedia('(min-width: 750px)');
    /** @type {HTMLElement[]} */
    #cards = [];
    /** @type {HTMLButtonElement | null} */
    #button = null;
    #shown = 0;

    connectedCallback() {
      this.#cards = Array.from(this.querySelectorAll('[data-review-card]'));
      this.#button = this.querySelector('[data-reviews-more]');
      if (!this.#cards.length || !this.#button) return;

      this.#shown = Math.max(1, Number(this.dataset.initial) || 5);
      this.#button.addEventListener('click', this.#reveal);
      this.#apply();
    }

    disconnectedCallback() {
      this.#button?.removeEventListener('click', this.#reveal);
    }

    get #step() {
      const key = this.#desktop.matches ? 'stepDesktop' : 'stepMobile';
      return Math.max(1, Number(this.dataset[key]) || 5);
    }

    #reveal = () => {
      const firstNew = this.#cards[this.#shown];
      this.#shown += this.#step;
      this.#apply();

      // Keep the reading position: send focus to the first card that just appeared, so the keyboard
      // carries on from there rather than jumping back to the top of the list.
      if (firstNew) {
        firstNew.setAttribute('tabindex', '-1');
        firstNew.focus({ preventScroll: true });
      }
    };

    #apply() {
      this.#cards.forEach((card, index) => {
        card.toggleAttribute('hidden', index >= this.#shown);
      });

      if (this.#button) this.#button.hidden = this.#shown >= this.#cards.length;
    }
  }

  customElements.define('sustena-reviews-more', SustenaReviewsMore);
}
